import { z } from 'zod';
import { FeatureType } from '../core/types.js';
/**
 * Build a (uiName, ZodTypeAny) pair for a def. Uses `flatModelSchema` when
 * present; otherwise wraps `def.schema` in `.default(def.default)` so the
 * field is optional on the input side. The per-branch switch (rather than a
 * single generic statement) preserves type correlation, avoiding an
 * `as never` at the z.object call site.
 */
function fieldEntry(def) {
    const { uiName } = def;
    if (def.flatModelSchema) {
        return [uiName, def.flatModelSchema];
    }
    switch (def.type) {
        case FeatureType.Boolean:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.String:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.Number:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.Markdown:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.MultiSelect:
            return [uiName, def.schema.default([...def.default])];
        case FeatureType.Json:
            return [uiName, def.schema.default(def.default)];
        case FeatureType.StringList:
            return [uiName, def.schema.default([...def.default])];
        default:
            return [uiName, z.unknown()];
    }
}
/**
 * Build a Zod object schema keyed by each def's `uiName`. Defs needing
 * shapes outside the registry's primitives (nullable values, narrow enums,
 * preprocess pipelines) opt in via `flatModelSchema`.
 *
 * `extras` adds keys that aren't registry features (e.g. synthetic computed
 * fields). Both params are inferred `const` so the returned ZodObject's
 * shape tracks each field's exact schema type, preserving autocomplete on
 * `z.infer<typeof model>`.
 */
export function buildFlatFeaturesModel(defs, extras = {}) {
    const shape = {};
    for (const def of defs) {
        const [key, schema] = fieldEntry(def);
        shape[key] = schema;
    }
    for (const [key, schema] of Object.entries(extras)) {
        shape[key] = schema;
    }
    return z.object(shape);
}
